import type { ClientType, CompanyType } from "@entrilia/shared/api/clientTypes";

export interface StartDwhSyncRequest {
  clientCode: string;
  startExportFromBc: boolean;
  importDataFromCore: boolean;
  recreateDatabaseSchema: boolean;
}

export interface StartDwhSyncResponse {
  previousSyncInProgress: boolean;
  correlationId?: string | null;
}

export interface ClientApplicationStateResponse {
  supported: boolean;
  enabled: boolean;
}

export interface ExcelAddinClientApplicationStateResponse {
  useBcReportGenerationViaSession: boolean;
}

export interface ClientProfileApplicationsResponse {
  investorPortal: ClientApplicationStateResponse;
  expenseManagement: ClientApplicationStateResponse;
  entriliaMcp: ClientApplicationStateResponse;
  portfolioMonitoring: ClientApplicationStateResponse;
  excelAddin: ExcelAddinClientApplicationStateResponse;
}

export interface ClientProfileApiResponse {
  clientId: string;
  clientName: string;
  clientCode: string;
  clientType: ClientType;
  companyType: CompanyType;
  sourceTemplateClientCode?: string;
  entityLevelAccessConfigured: boolean;
  managedByClients: string[];
  managesClients: string[];
  applications: ClientProfileApplicationsResponse;
}

export interface DemoSourceTemplateCandidate {
  clientCode: string;
  title: string;
  location: string;
}

export interface DemoSourceTemplateCandidatesResponse {
  items: DemoSourceTemplateCandidate[];
}

export interface SetDemoSourceTemplateRequest {
  targetClientCode: string;
  sourceTemplateClientCode?: string;
}

export interface SetApplicationStateRequest {
  clientCode: string;
  enabled: boolean;
}

export interface CreateFundAdminClientRequest {
  continueIfClientAlreadyExists: boolean;
  title: string;
  clientCode: string;
  usLocation: boolean;
  organizationDomains: string[];
  sherwebCustomerId: string;
  websiteUrl?: string;
  primaryBcTenantId: string;
  primaryBcEnvironment: string;
}

export interface CreateFundManagerClientRequest {
  continueIfClientAlreadyExists: boolean;
  organizationClientCode: string;
  title: string;
  clientCode: string;
  enableInvestorPortal: boolean;
  enableExpenseManagement: boolean;
  enablePortfolioMonitoringDataCollection: boolean;
  businessCentralApp?: {
    companyId: string;
    companyNameOverride: string;
    environmentOverride: string;
    tenantIdOverride: string;
  };
  websiteUrl?: string;
  companyType: CompanyType;
}

export interface OrganizationInfo {
  id: string;
  clientCode: string;
  name: string;
  location: EntriliaServicesRegion;
  managesClients: CompanyInfo[];
  // Entrilia's own umbrella orgs (co-manage every client); excluded when resolving a client's real fund admin.
  isInternalOrg: boolean;
}

export interface CompanyInfo {
  id: string;
  clientCode: string;
  name: string;
  type: ClientType;
  bcCompanyId: string;
  bcTenantId: string | null;
  bcEnvironment: string | null;
  companyType: CompanyType;
  enableInvestorPortal: boolean;
  enableExpenseManagement: boolean;
  enablePortfolioMonitoringDataCollection: boolean;
}

type EntriliaServicesRegion = "EastUS" | "Netherlands";

export interface BcTenantSelection {
  tenantId: string;
  environment: string;
}

export interface BcTenantOption extends BcTenantSelection {
  displayName: string;
}

export interface BcTenantCompanies {
  tenantId: string | null;
  environment: string | null;
  companies: BcCompany[];
  availableTenants: BcTenantOption[];
}

export interface BcCompany {
  name: string;
  displayName: string;
  companyId: string;
}

export interface SherWebOrganizationInfo {
  id: string;
  displayName: string;
}

export interface ChangeClientEventHubsClientsResponse {
  clientsByTarget: Record<ChangeCompanyEventHubsRequestTarget, string[]>;
}

export interface ChangeClientEventHubsRequest {
  clientCode: string;
  target: ChangeCompanyEventHubsRequestTarget;
}

export interface ChangeClientEventHubsResponse {
  success: boolean;
  postChangeInstructions: string;
}

export type ChangeCompanyEventHubsRequestTarget = "PerOrganizationEventHubs" | "DedicatedPerCompanyEventHubs";

export const companyEventHubsCaptions: Record<ChangeCompanyEventHubsRequestTarget, string> = {
  PerOrganizationEventHubs: "Per-organization",
  DedicatedPerCompanyEventHubs: "Dedicated per-company",
};

export interface CreateInvestorPortalUserFromContactRequest {
  email: string;
  clientCode: string;
  mfaStatus: UserMfaStatus;
}

export interface CreateInvestorPortalUserResponse {
  userId: string;
  signInEmail: string;
  displayName: string;
  temporaryPassword: string;
  userCreated: boolean;
  mfaApplied: boolean;
  warnings: string[];
}

export const allUserMfaStatuses = ["NoMFA", "ConditionalMFA", "MandatoryMFA"] as const;
export type UserMfaStatus = (typeof allUserMfaStatuses)[number];

export const userMfaStatusCaptions: Record<UserMfaStatus, string> = {
  NoMFA: "No MFA",
  ConditionalMFA: "Conditional MFA",
  MandatoryMFA: "Mandatory MFA",
};

export interface UserExistsRequest {
  email: string;
}

export interface UserExistsResponse {
  userExists: boolean;
  email: string;
}

export interface GetInvestorPortalUserStatusRequest {
  email: string;
}

export interface InvestorPortalClientAccess {
  clientCode: string;
  clientTitle: string;
}

export interface GetInvestorPortalUserStatusResponse {
  email: string;
  userExists: boolean;
  canResetPassword: boolean;
  clients: InvestorPortalClientAccess[];
}

export interface ResetInvestorPortalUserPasswordRequest {
  email: string;
}

export interface ResetInvestorPortalUserPasswordResponse {
  email: string;
  tempPassword: string;
}

export interface CopyCompanyRequest {
  sourceClientCode: string;
  targetClientCode: string;
  targetBcCompanyName: string;
  targetBcCompanyDisplayName: string;
  targetBcCompanyId?: string;
  newClient?: CopyCompanyNewClientInfo;
}

export interface CopyCompanyNewClientInfo {
  title: string;
  companyType: CompanyType;
  enableInvestorPortal: boolean;
  enableExpenseManagement: boolean;
  enablePortfolioMonitoringDataCollection: boolean;
  websiteUrl?: string;
}

export interface DeprovisionFundManagerClientRequest {
  clientCode: string;
  deleteContacts?: boolean;
  deleteInvestors?: boolean;
  deleteFunds?: boolean;
  deleteOtherData?: boolean;
}

export interface SyncFundAdminBcPermissionsRequest {
  organizationClientCode: string;
}
